import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Slider,
  IconButton,
  Divider,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { MuiColorInput } from 'mui-color-input';
import AccessibleIcon from '@mui/icons-material/Accessible';
import UseToast from '../../UseToast';

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '1000px',
  maxHeight: '90vh',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '16px',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  overflowY: 'auto',
}));

const Header = styled(Box)({
  background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
  borderRadius: '16px 16px 0 0',
  padding: '24px 32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const Body = styled(Box)({
  padding: '32px',
});

const SectionCard = styled(Box)({
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  padding: '20px',
  marginBottom: '20px',
});

const SectionTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '13px',
  textTransform: 'uppercase',
  letterSpacing: '1.2px',
  color: '#64748b',
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const PreviewContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(2),

  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '250px',
  position: 'relative',
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
}));

const UploadBox = styled(Box)({
  border: '2px dashed #cbd5e1',
  borderRadius: '12px',
  padding: '32px',
  textAlign: 'center',
  backgroundColor: '#ffffff',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#6366f1',
    backgroundColor: '#f8fafc',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
  },
});

const WheelchairCustomizationModal = ({ open, onClose, userId, brandColor, onSave }) => {
  const { showMsgSuccessToast, showErrorToast } = UseToast();

  const [settings, setSettings] = useState({
    enabled: false,
    iconType: 'wheelchair',
    customIconUrl: '',
    customIconFileName: '',
    iconSize: 60,
    iconColor: '#0066CC',
    iconActiveColor: '#00AA00',
    positionTop: null,
    positionBottom: 20,
    positionLeft: null,
    positionRight: 20,
    backgroundColor: '#FFFFFF',
    backgroundOpacity: 90,
    borderRadius: 50,
    borderWidth: 2,
    borderColor: '#0066CC',
    padding: 12,
    shadowH: 0,
    shadowV: 4,
    shadowBlur: 12,
    shadowSpread: 0,
    shadowColor: '#000000',
    shadowOpacity: 25,
    hoverScale: 1.1,
    transitionDuration: 0.3,
  });

  const [previewActive, setPreviewActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (open && userId) {
      fetchSettings();
    }
  }, [open, userId]);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`/api/wheelchair-settings/get/${userId}`);
      const data = await response.json();
      if (data.success && data.data) {
        setSettings(data.data);
      }
    } catch (error) {
      console.error('Error fetching wheelchair settings:', error);
    }
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`/api/wheelchair-settings/save/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });

      const data = await response.json();

      if (data.success) {
        showMsgSuccessToast('Wheelchair settings saved successfully');
        if (onSave) {
          onSave(settings);
        }
        onClose();
      } else {
        showErrorToast(data.message || 'Failed to save wheelchair settings');
      }
    } catch (error) {
      console.error('Error saving wheelchair settings:', error);
      showErrorToast('Failed to save wheelchair settings');
    }
  };

  const handleChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showErrorToast('Please upload a valid image file (PNG, JPG, SVG, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showErrorToast('File size must be less than 5MB');
      return;
    }

    setSelectedFile(file);
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);

    try {
      // Get signed URL
      const response = await fetch(
        `/generate-signed-url?fileName=${encodeURIComponent(selectedFile.name)}&fileType=${encodeURIComponent(selectedFile.type)}`
      );
      const { url, publicUrl } = await response.json();

      // Upload file to S3
      await fetch(url, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': selectedFile.type,
        },
      });

      // Update settings with the uploaded file URL
      setSettings((prev) => ({
        ...prev,
        customIconUrl: publicUrl,
        customIconFileName: selectedFile.name,
        iconType: 'custom',
      }));

      showMsgSuccessToast('Icon uploaded successfully');
      setSelectedFile(null);
    } catch (error) {
      console.error('Error uploading icon:', error);
      showErrorToast('Failed to upload icon');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveCustomIcon = () => {
    setSettings((prev) => ({
      ...prev,
      customIconUrl: '',
      customIconFileName: '',
      iconType: 'wheelchair',
    }));
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getPreviewButtonStyle = () => {
    const bgColor = settings.backgroundColor;
    const bgOpacity = settings.backgroundOpacity / 100;
    const r = parseInt(bgColor.slice(1, 3), 16);
    const g = parseInt(bgColor.slice(3, 5), 16);
    const b = parseInt(bgColor.slice(5, 7), 16);

    const shadowColor = settings.shadowColor;
    const shadowOpacity = settings.shadowOpacity / 100;
    const sr = parseInt(shadowColor.slice(1, 3), 16);
    const sg = parseInt(shadowColor.slice(3, 5), 16);
    const sb = parseInt(shadowColor.slice(5, 7), 16);

    return {
      width: `${settings.iconSize + settings.padding * 2}px`,
      height: `${settings.iconSize + settings.padding * 2}px`,
      backgroundColor: `rgba(${r}, ${g}, ${b}, ${bgOpacity})`,
      borderRadius: `${settings.borderRadius}%`,
      border: `${settings.borderWidth}px solid ${settings.borderColor}`,
      padding: `${settings.padding}px`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: `all ${settings.transitionDuration}s ease`,
      boxShadow: `${settings.shadowH}px ${settings.shadowV}px ${settings.shadowBlur}px ${settings.shadowSpread}px rgba(${sr}, ${sg}, ${sb}, ${shadowOpacity})`,
      transform: previewActive ? `scale(${settings.hoverScale})` : 'scale(1)',
    };
  };

  const accentColor = brandColor || '#6366f1';

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContent>
        {/* Header */}
        <Header>
          <Box>
            <Typography variant="h5" fontWeight={700} color="white" lineHeight={1.2}>
              ♿ Wheelchair Accessibility Settings
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.7)" sx={{ mt: 0.5 }}>
              Customize the wheelchair accessibility toggle button
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              '&:hover': {
                color: 'white',
                background: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Header>

        <Body>
          {/* Enable/Disable */}
          <SectionCard>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enabled}
                  onChange={(e) => handleChange('enabled', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: accentColor },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: accentColor },
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    Enable Wheelchair Accessibility Toggle
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Show a button that allows users to toggle wheelchair-accessible content
                  </Typography>
                </Box>
              }
            />
          </SectionCard>

          {settings.enabled && (
            <>
              {/* Icon Settings */}
              <SectionCard>
                <SectionTitle>🎨 Icon Settings</SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Icon Type</InputLabel>
                      <Select
                        value={settings.iconType}
                        label="Icon Type"
                        onChange={(e) => handleChange('iconType', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e2e8f0',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: accentColor,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: accentColor,
                          },
                        }}
                      >
                        <MenuItem value="wheelchair">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessibleIcon />
                            <span>Wheelchair Icon (Default)</span>
                          </Box>
                        </MenuItem>
                        <MenuItem value="custom">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>🖼️</span>
                            <span>Upload Custom Icon</span>
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {settings.iconType === 'custom' && (
                    <Grid item xs={12}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                        id="wheelchair-icon-upload"
                      />

                      {!settings.customIconUrl && !selectedFile ? (
                        <label htmlFor="wheelchair-icon-upload">
                          <UploadBox>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="h3" sx={{ mb: 1 }}>📤</Typography>
                              <Typography variant="h6" fontWeight={600} color="text.primary" gutterBottom>
                                Select Custom Icon
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Click to browse and select your icon file
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                              Supported formats: PNG, JPG, SVG, WebP (Max 5MB)
                            </Typography>
                          </UploadBox>
                        </label>
                      ) : selectedFile && !settings.customIconUrl ? (
                        <Box
                          sx={{
                            border: '2px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '20px',
                            backgroundColor: '#ffffff',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f8fafc',
                              }}
                            >
                              <Typography variant="h4">📄</Typography>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body1" fontWeight={600}>
                                {selectedFile.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {(selectedFile.size / 1024).toFixed(2)} KB
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="contained"
                              onClick={handleFileUpload}
                              disabled={uploading}
                              sx={{
                                backgroundColor: accentColor,
                                '&:hover': { backgroundColor: accentColor, opacity: 0.9 },
                              }}
                            >
                              {uploading ? 'Uploading...' : 'Upload Icon'}
                            </Button>
                            <Button
                              variant="outlined"
                              component="label"
                              htmlFor="wheelchair-icon-upload"
                              disabled={uploading}
                            >
                              Choose Different File
                            </Button>
                          </Box>
                        </Box>
                      ) : settings.customIconUrl ? (
                        <Box
                          sx={{
                            border: '2px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '20px',
                            backgroundColor: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f8fafc',
                                overflow: 'hidden',
                              }}
                            >
                              <img
                                src={settings.customIconUrl}
                                alt="Custom icon preview"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                }}
                              />
                            </Box>
                            <Box>
                              <Typography variant="body1" fontWeight={600}>
                                {settings.customIconFileName || 'Custom Icon'}
                              </Typography>
                              <Typography variant="caption" color="success.main">
                                ✓ Uploaded successfully
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="outlined"
                              component="label"
                              htmlFor="wheelchair-icon-upload"
                              disabled={uploading}
                              size="small"
                            >
                              Replace
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={handleRemoveCustomIcon}
                              disabled={uploading}
                              size="small"
                            >
                              Remove
                            </Button>
                          </Box>
                        </Box>
                      ) : null}
                    </Grid>
                  )}

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Icon Size (px)"
                      value={settings.iconSize}
                      onChange={(e) => handleChange('iconSize', Number(e.target.value))}
                      inputProps={{ min: 20, max: 200 }}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MuiColorInput
                      label="Icon Color (Inactive)"
                      value={settings.iconColor}
                      onChange={(value) => handleChange('iconColor', value)}
                      format="hex"
                      isAlphaHidden
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MuiColorInput
                      label="Icon Color (Active)"
                      value={settings.iconActiveColor}
                      onChange={(value) => handleChange('iconActiveColor', value)}
                      format="hex"
                      isAlphaHidden
                      size="small"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              {/* Position Settings */}
              <SectionCard>
                <SectionTitle>📍 Position Settings (% from edge)</SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Top (%)"
                      value={settings.positionTop ?? ''}
                      onChange={(e) => handleChange('positionTop', e.target.value ? Number(e.target.value) : null)}
                      placeholder="Auto"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Bottom (%)"
                      value={settings.positionBottom ?? ''}
                      onChange={(e) => handleChange('positionBottom', e.target.value ? Number(e.target.value) : null)}
                      placeholder="Auto"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Left (%)"
                      value={settings.positionLeft ?? ''}
                      onChange={(e) => handleChange('positionLeft', e.target.value ? Number(e.target.value) : null)}
                      placeholder="Auto"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Right (%)"
                      value={settings.positionRight ?? ''}
                      onChange={(e) => handleChange('positionRight', e.target.value ? Number(e.target.value) : null)}
                      placeholder="Auto"
                      size="small"
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              {/* Background & Border */}
              <SectionCard>
                <SectionTitle>🎨 Background & Border</SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <MuiColorInput
                      label="Background Color"
                      value={settings.backgroundColor}
                      onChange={(value) => handleChange('backgroundColor', value)}
                      format="hex"
                      isAlphaHidden
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Background Opacity (%)"
                      value={settings.backgroundOpacity}
                      onChange={(e) => handleChange('backgroundOpacity', Number(e.target.value))}
                      inputProps={{ min: 0, max: 100 }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Border Radius (%)"
                      value={settings.borderRadius}
                      onChange={(e) => handleChange('borderRadius', Number(e.target.value))}
                      inputProps={{ min: 0, max: 100 }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Border Width (px)"
                      value={settings.borderWidth}
                      onChange={(e) => handleChange('borderWidth', Number(e.target.value))}
                      inputProps={{ min: 0, max: 10 }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MuiColorInput
                      label="Border Color"
                      value={settings.borderColor}
                      onChange={(value) => handleChange('borderColor', value)}
                      format="hex"
                      isAlphaHidden
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Padding (px)"
                      value={settings.padding}
                      onChange={(e) => handleChange('padding', Number(e.target.value))}
                      inputProps={{ min: 0, max: 50 }}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              {/* Shadow Settings */}
              <SectionCard>
                <SectionTitle>✨ Shadow Settings</SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Horizontal Offset (px)"
                      value={settings.shadowH}
                      onChange={(e) => handleChange('shadowH', Number(e.target.value))}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Vertical Offset (px)"
                      value={settings.shadowV}
                      onChange={(e) => handleChange('shadowV', Number(e.target.value))}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Blur Radius (px)"
                      value={settings.shadowBlur}
                      onChange={(e) => handleChange('shadowBlur', Number(e.target.value))}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Spread Radius (px)"
                      value={settings.shadowSpread}
                      onChange={(e) => handleChange('shadowSpread', Number(e.target.value))}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MuiColorInput
                      label="Shadow Color"
                      value={settings.shadowColor}
                      onChange={(value) => handleChange('shadowColor', value)}
                      format="hex"
                      isAlphaHidden
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Shadow Opacity (%)"
                      value={settings.shadowOpacity}
                      onChange={(e) => handleChange('shadowOpacity', Number(e.target.value))}
                      inputProps={{ min: 0, max: 100 }}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              {/* Animation Settings */}
              <SectionCard>
                <SectionTitle>🎬 Animation Settings</SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Hover Scale"
                      value={settings.hoverScale}
                      onChange={(e) => handleChange('hoverScale', Number(e.target.value))}
                      inputProps={{ min: 1, max: 2, step: 0.1 }}

                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Transition Duration (seconds)"
                      value={settings.transitionDuration}
                      onChange={(e) => handleChange('transitionDuration', Number(e.target.value))}
                      inputProps={{ min: 0, max: 2, step: 0.1 }}

                      size="small"
                    />
                  </Grid>
                </Grid>
              </SectionCard>

              {/* Preview */}
              <SectionCard>
                <SectionTitle>👁️ Live Preview</SectionTitle>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Hover over the button to see the active state
                </Typography>
                <PreviewContainer>
                  <div
                    style={getPreviewButtonStyle()}
                    onMouseEnter={() => setPreviewActive(true)}
                    onMouseLeave={() => setPreviewActive(false)}
                  >
                    {settings.iconType === 'custom' && settings.customIconUrl ? (
                      <img
                        src={settings.customIconUrl}
                        alt="Custom wheelchair icon"
                        style={{
                          width: `${settings.iconSize}px`,
                          height: `${settings.iconSize}px`,
                          objectFit: 'contain',
                          borderRadius: `${settings.borderRadius}%`,
                        }}
                      />
                    ) : (
                      <AccessibleIcon
                        sx={{
                          fontSize: `${settings.iconSize}px`,
                          color: previewActive ? settings.iconActiveColor : settings.iconColor,
                          transition: `color ${settings.transitionDuration}s ease`,
                        }}
                      />
                    )}
                  </div>
                </PreviewContainer>
              </SectionCard>
            </>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                borderColor: '#e2e8f0',
                color: '#64748b',
                '&:hover': {
                  borderColor: '#cbd5e1',
                  backgroundColor: '#f8fafc',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                backgroundColor: accentColor,
                boxShadow: `0 4px 12px ${accentColor}40`,
                '&:hover': {
                  backgroundColor: accentColor,
                  opacity: 0.9,
                  boxShadow: `0 6px 16px ${accentColor}50`,
                },
              }}
            >
              Save Settings
            </Button>
          </Box>
        </Body>
      </ModalContent>
    </Modal>
  );
};

export default WheelchairCustomizationModal;
